/**
 * `vendors.*` — supplier master CRUD + archive + bank-detail step-up.
 *
 * Master fields (name, GSTIN, contact, address, terms, TDS section) ride on
 * the regular `tenantedProcedure` flavour. **Bank details** are a separate
 * procedure (`changeBankDetails`) that runs on `stepUpFounder` per
 * system-decisions §1, §14 and ADR-0008 — they never round-trip through
 * `update`.
 *
 * Architecture: each procedure does parse → call domain → map errors →
 * return result (architecture §6.1).
 *
 * Vendor master, like customers, is mutable; archive replaces hard delete
 * because `bills.vendor_id` and `vendor_payments.vendor_id` have
 * `onDelete: 'restrict'`. ADR-0008 carries the rationale.
 */
import { z } from "zod";
import {
  uuid,
  paged,
  gstin,
  phoneIN,
  pin,
  indianState,
  moneyPaise,
  signedPaise,
  isoDate,
} from "../_common.js";

/** GST registration class. Locked at the DB layer by a CHECK constraint
 *  in migration 0040 — keep this enum in sync. */
export const vendorRegistrationType = z.enum([
  "regular",
  "composition",
  "unregistered",
  "casual",
  "sez",
]);

/** Payment mode the operator typically pays this vendor through. Mirrors
 *  `vendorPaymentMode` in vendor-payments.ts (same enum on the wire). */
export const vendorDefaultPaymentMode = z.enum([
  "neft",
  "rtgs",
  "imps",
  "upi",
  "cheque",
  "cash",
  "card",
  "other",
]);

/**
 * Patchable master fields (all share-able between create + update). Bank
 * fields are deliberately absent — they go through `changeBankDetails`.
 */
const baseFields = z.object({
  /** Display name on the supplier side. The only required field. */
  name: z.string().trim().min(1, "Name is required").max(255),
  contactName: z.string().trim().min(1).max(255).optional(),
  gstin: gstin.optional(),
  phone: phoneIN.optional(),
  email: z.string().email().optional(),
  addressLine1: z.string().trim().min(1).max(255).optional(),
  addressLine2: z.string().trim().min(1).max(255).optional(),
  state: indianState.optional(),
  city: z.string().trim().min(1).max(64).optional(),
  pincode: pin.optional(),
  /** TDS section (`194Q`, `194C`, …). Free-form so a new section doesn't break the schema. */
  tdsSection: z.string().trim().max(16).optional(),
  /** §143 job-worker flag — used by ITC-04 to pick up posted job-worker bills. */
  isJobWorker: z.boolean().optional(),
  paymentTerms: z.string().trim().max(32).optional(),

  // ── V1.1 composer extension fields (migration 0040) ─────────────

  /** Short label for lists / dropdowns / briefings. Falls back to `name`. */
  displayName: z.string().trim().max(120).optional(),
  registrationType: vendorRegistrationType.optional(),
  /** Owner / Accounts / Operations / Sales / Other — free string. */
  contactRole: z.string().trim().max(32).optional(),
  /** Operator-confirmed: the contact number reaches WhatsApp. */
  phoneWhatsapp: z.boolean().optional(),
  defaultPaymentMode: vendorDefaultPaymentMode.optional(),
  /** Reverse-charge default for bills from this vendor. */
  rcmDefault: z.boolean().optional(),
  /** Input-tax-credit eligible default. Off only for §17(5) blocks. */
  itcEligible: z.boolean().optional(),
  /** Udyam registration — drives the MSMED 45-day payment-cap warning. */
  msmeRegistered: z.boolean().optional(),
  /** Vendor category — free string for V1 (no master table yet). */
  vendorCategory: z.string().trim().max(64).optional(),
  /** GL head pre-fill — free string for V1 (no chart-of-accounts yet). */
  defaultGlAccount: z.string().trim().max(64).optional(),
  /** Operator-only filter tags. Trimmed + deduped client-side. */
  tags: z.array(z.string().trim().min(1).max(64)).max(32).optional(),
  /** Pinned team note — never on vendor-facing surfaces. */
  internalNote: z.string().trim().max(2000).optional(),
});

// ── create ────────────────────────────────────────────────────────
export const createInput = baseFields;
export const createOutput = z.object({ id: uuid });

// ── get one ───────────────────────────────────────────────────────
export const getInput = z.object({ id: uuid });

/**
 * Full vendor row as returned to the SPA. Bank-detail ciphers are NOT on
 * this shape — there's a separate read path that an Operator-tier session
 * receives masked, and the Founder-tier sees only via a step-up flow.
 */
export const vendorRow = z.object({
  id: uuid,
  name: z.string(),
  contactName: z.string().nullable(),
  gstin: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  pincode: z.string().nullable(),
  tdsSection: z.string().nullable(),
  isJobWorker: z.boolean(),
  paymentTerms: z.string().nullable(),

  // ── V1.1 composer extension fields (migration 0040) ─────────────

  displayName: z.string().nullable(),
  /** GST registration class — see `vendorRegistrationType`. */
  registrationType: vendorRegistrationType,
  contactRole: z.string().nullable(),
  phoneWhatsapp: z.boolean(),
  defaultPaymentMode: vendorDefaultPaymentMode.nullable(),
  rcmDefault: z.boolean(),
  itcEligible: z.boolean(),
  msmeRegistered: z.boolean(),
  vendorCategory: z.string().nullable(),
  defaultGlAccount: z.string().nullable(),
  tags: z.array(z.string()),
  internalNote: z.string().nullable(),

  /** ISO timestamp of the last bank-detail change. Drives the cooling-off check. */
  bankDetailsChangedAt: z.string().datetime().nullable(),
  /** True iff the vendor row carries any encrypted bank ciphertext today. */
  hasBankDetails: z.boolean(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export const getOutput = vendorRow;

// ── update (master fields only) ───────────────────────────────────
/**
 * Patch fields are all optional (`.partial()`); `id` selects the row.
 * **Bank fields are intentionally absent** — see `changeBankDetailsInput`.
 */
export const updateInput = baseFields.partial().extend({ id: uuid });
export const updateOutput = z.object({ id: uuid });

// ── archive ───────────────────────────────────────────────────────
export const archiveInput = z.object({
  id: uuid,
  reason: z.string().trim().max(255).optional(),
});
export const archiveOutput = z.object({
  id: uuid,
  archivedAt: z.string().datetime(),
});

// ── change bank details (step-up founder) ─────────────────────────
/**
 * Mutation surface for bank-detail change. Runs on `stepUpFounder` —
 * Founder role + a fresh OTP within the last 5 minutes (system-decisions
 * §1, code-patterns §7). After the write, the dispatcher enforces a
 * 24-hour cooling-off before any payment can be released to the new
 * account (ADR-0008).
 *
 * V1.0.x ships the surface; the encrypted writes + dispatcher check
 * land alongside the payments vertical.
 */
export const changeBankDetailsInput = z.object({
  id: uuid,
  /** Indian bank account number — 9 to 18 digits. */
  bankAccount: z.string().regex(/^\d{9,18}$/, "invalid bank account number"),
  /** RBI IFSC — `4 letters + 0 + 6 alphanumerics`. */
  ifsc: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "invalid IFSC"),
  /** Optional human-readable reason — lands on the audit row's metadata. */
  reason: z.string().trim().max(255).optional(),
});
export const changeBankDetailsOutput = z.object({
  id: uuid,
  /** The cooling-off start (== the change timestamp). */
  bankDetailsChangedAt: z.string().datetime(),
  /** The cooling-off end (`bankDetailsChangedAt + 24h`). */
  coolingOffUntil: z.string().datetime(),
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  q: z.string().optional(),
  /**
   * `'all'` excludes archived; `'archived'` shows only archived. Other
   * tabs in the prototype (`dues`, `active`, `recurring`, `new`) are
   * UI-only filters that the SPA applies on the client until the
   * downstream verticals (bills, payments) land.
   */
  tab: z.enum(["all", "archived"]).default("all"),
});

/**
 * One row in the list response. Lean — list views fetch many; detail
 * fetches one. Bank fields aren't on this shape; the SPA renders a "bank
 * details" affordance only on the detail page.
 */
export const listRow = z.object({
  id: uuid,
  name: z.string(),
  contactName: z.string().nullable(),
  gstin: z.string().nullable(),
  phone: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  tdsSection: z.string().nullable(),
  isJobWorker: z.boolean(),
  paymentTerms: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  // Aggregations from bills. Until this landed, the SPA hardcoded all
  // three to "₹0 · no bills" with TODO comments — every vendor read as
  // dead even when active.
  /** Sum of total_paise on bills in review / approval_pending / ready_to_pay. */
  outstandingPaise: moneyPaise,
  /** Sum of total_paise on paid bills with received_on in the current Indian FY. */
  ytdSpentPaise: moneyPaise,
  /** Count of bills excluding draft + void. */
  billCount: z.number().int().nonnegative(),
  /** ISO date of the most recent bill (received_on); null if no bills. */
  lastActivityAt: isoDate.nullable(),
});

export const listOutput = paged(listRow);

// ── export ────────────────────────────────────────────────────────
/**
 * Filter for the export surfaces — the SAME `q`/`tab` the list toolbar
 * carries, so "Export" honours the operator's current view. No cursor:
 * export always spans the whole filtered set (capped server-side).
 */
export const exportFilter = z.object({
  q: z.string().optional(),
  tab: z.enum(["all", "archived"]).default("all"),
});

/**
 * One flattened vendor row for the CSV / Excel / PDF export. Richer than
 * `listRow` — the export is the operator's book of record, so it carries
 * the master fields a spreadsheet audit needs (email, full address, GST
 * flags) that the on-screen list omits. Money stays integer paise on the
 * wire; the client formats to ₹ at the render seam.
 */
export const exportRow = z.object({
  name: z.string(),
  displayName: z.string().nullable(),
  gstin: z.string().nullable(),
  registrationType: vendorRegistrationType,
  contactName: z.string().nullable(),
  contactRole: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  pincode: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  tdsSection: z.string().nullable(),
  isJobWorker: z.boolean(),
  msmeRegistered: z.boolean(),
  itcEligible: z.boolean(),
  rcmDefault: z.boolean(),
  defaultPaymentMode: vendorDefaultPaymentMode.nullable(),
  vendorCategory: z.string().nullable(),
  tags: z.array(z.string()),
  outstandingPaise: moneyPaise,
  ytdSpentPaise: moneyPaise,
  billCount: z.number().int().nonnegative(),
  lastActivityAt: isoDate.nullable(),
  status: z.enum(["active", "archived"]),
  createdAt: z.string().datetime(),
});

/** `exportRows` — the full filtered vendor set for client-side CSV/Excel. */
export const exportRowsInput = exportFilter;
export const exportRowsOutput = z.object({
  rows: z.array(exportRow),
  /** ISO timestamp the server assembled the set — stamped onto exports. */
  generatedAt: z.string().datetime(),
});

/** `exportPdf` — server renders the filtered set to a PDF via Gotenberg. */
export const exportPdfInput = exportFilter;
export const exportPdfOutput = z.object({
  fileName: z.string(),
  /** PDF bytes, base64-encoded for the JSON wire (same shape as reports). */
  pdfBase64: z.string(),
});

// ── import (simple bulk-create) ───────────────────────────────────
/**
 * Bulk vendor import. Each row is a full `createInput` — the SPA parses
 * the CSV and validates every row against `createInput` client-side, so
 * only well-formed rows reach the wire. The server still re-validates at
 * the tRPC boundary and enforces GSTIN uniqueness (in-batch + against the
 * existing master), skipping — never failing — duplicates.
 */
export const importInput = z.object({
  rows: z.array(createInput).min(1).max(1000),
});

/** Per-row outcome, index-aligned to the submitted `rows`. */
export const importResultRow = z.object({
  index: z.number().int().nonnegative(),
  name: z.string(),
  status: z.enum(["created", "skipped", "failed"]),
  id: uuid.optional(),
  /** Human-readable reason when `status` is `skipped` or `failed`. */
  message: z.string().optional(),
});

export const importOutput = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  results: z.array(importResultRow),
});

// ── ledger ────────────────────────────────────────────────────────
/**
 * Read-only chronological ledger for one vendor. Unions this vendor's bills
 * and live payments into a running-balance table. Powers the vendor detail
 * "Ledger" tab. Running balance is signed paise: **positive = we owe the
 * vendor**, negative = the vendor holds our advance / credit.
 */
export const ledgerInput = z.object({ id: uuid });

export const ledgerRow = z.object({
  kind: z.enum(["bill", "payment"]),
  entryId: uuid,
  at: isoDate,
  /** Vendor doc number / payment reference ("" when absent). */
  docNumber: z.string(),
  /** Positive stringified paise. "0" if this row is credit-side (a payment). */
  debitPaise: moneyPaise,
  /** Positive stringified paise. "0" if this row is debit-side (a bill). */
  creditPaise: moneyPaise,
  /** Signed stringified paise. Positive = we owe the vendor. */
  runningBalancePaise: signedPaise,
  /** Bill status ("paid"/"ready_to_pay"/etc); payment status for payments. */
  status: z.string().nullable(),
});

export const ledgerOutput = z.object({
  /** Opening balance — 0 for vendors in V1 (no migration opening column). */
  openingBalancePaise: signedPaise,
  /** Balance after all rows applied. Signed paise. */
  closingBalancePaise: signedPaise,
  rows: z.array(ledgerRow),
});

// ── derived TS types (consumed by domain + web) ───────────────────
export type CreateInput = z.infer<typeof createInput>;
export type CreateOutput = z.infer<typeof createOutput>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type UpdateInput = z.infer<typeof updateInput>;
export type UpdateOutput = z.infer<typeof updateOutput>;
export type ArchiveInput = z.infer<typeof archiveInput>;
export type ArchiveOutput = z.infer<typeof archiveOutput>;
export type ChangeBankDetailsInput = z.infer<typeof changeBankDetailsInput>;
export type ChangeBankDetailsOutput = z.infer<typeof changeBankDetailsOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ListRow = z.infer<typeof listRow>;
export type VendorRow = z.infer<typeof vendorRow>;
export type ExportFilter = z.infer<typeof exportFilter>;
export type ExportRow = z.infer<typeof exportRow>;
export type ExportRowsInput = z.infer<typeof exportRowsInput>;
export type ExportRowsOutput = z.infer<typeof exportRowsOutput>;
export type ExportPdfInput = z.infer<typeof exportPdfInput>;
export type ExportPdfOutput = z.infer<typeof exportPdfOutput>;
export type ImportInput = z.infer<typeof importInput>;
export type ImportResultRow = z.infer<typeof importResultRow>;
export type LedgerInput = z.infer<typeof ledgerInput>;
export type LedgerRow = z.infer<typeof ledgerRow>;
export type LedgerOutput = z.infer<typeof ledgerOutput>;
export type ImportOutput = z.infer<typeof importOutput>;
